import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Messages from './Messages';
import Videos from './Videos';
import EventsDashboard from './EventsDashboard';
import AdminPanelWithTabs from './AdminPanelWithTabs';

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('messages');
  const location = useLocation();
  const navigate = useNavigate();
  const hasVideoAccess = new Set(['family', 'family-plus', 'jga-plus']).has(user?.plan);

  useEffect(() => {
    const path = location.pathname.split('/')[1];

    if (path === 'videos') {
      setActiveTab('videos');
    } else if (path === 'events') {
      setActiveTab('events');
    } else if (path === 'triggers') {
      setActiveTab('triggers');
    } else if (path === 'keys') {
      setActiveTab('keys');
    } else if (path === 'bank') {
      setActiveTab('bank');
    } else if (path === 'settings') {
      setActiveTab('settings');
    } else if (path === 'admin') {
      setActiveTab('admin');
    } else {
      setActiveTab('messages');
    }
  }, [location.pathname]);

  const jwtToken = localStorage.getItem('timeLockToken');
  const adminToken = localStorage.getItem('adminToken');

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>TimeLock</h1>
          <div className="user-info">
            <span>{user.email}</span>
            <span className="plan-badge">{user.plan}</span>
            {user.admin === 1 && <span className="admin-badge">ADMIN</span>}
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => navigate('/messages')}
          >
            Messages
          </button>
          {hasVideoAccess && (
            <button
              className={`tab ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => navigate('/videos')}
            >
              Videos
            </button>
          )}
          <button
            className={`tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => navigate('/events')}
          >
            Events
          </button>
          <button
            className={`tab ${activeTab === 'triggers' ? 'active' : ''}`}
            onClick={() => navigate('/triggers')}
          >
            Triggers
          </button>
          <button
            className={`tab ${activeTab === 'keys' ? 'active' : ''}`}
            onClick={() => navigate('/keys')}
          >
            Keys
          </button>
          <button
            className={`tab ${activeTab === 'bank' ? 'active' : ''}`}
            onClick={() => navigate('/bank')}
          >
            Bank
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => navigate('/settings')}
          >
            Settings
          </button>
          {user.admin === 1 && (
            <button
              className={`tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => navigate('/admin')}
            >
              Admin Panel
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'messages' && <Messages user={user} />}
          {activeTab === 'videos' && <Videos user={user} />}
          {activeTab === 'events' && <EventsDashboard user={user} />}

          {activeTab === 'triggers' && (
            <div className="tab-panel">
              <h2>Triggers</h2>
              <p>Configure your legacy triggers here.</p>
              <div className="triggers-list">
                <div className="trigger-item">
                  <p className="no-items">No triggers configured yet</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'keys' && (
            <div className="tab-panel">
              <h2>Security Keys (QR/NFC)</h2>
              <p>Manage your security keys and authentication methods.</p>
              <div className="keys-list">
                <div className="key-item">
                  <p className="no-items">No keys configured yet</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bank' && (
            <div className="tab-panel">
              <h2>Bank Information</h2>
              <p>Manage your financial legacy information.</p>
              <div className="bank-list">
                <div className="bank-item">
                  <p className="no-items">No bank information added yet</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="tab-panel">
              <h2>Settings</h2>
              <div className="settings-section">
                <h3>Account Settings</h3>
                <p>Email: {user.email}</p>
                <p>Plan: {user.plan}</p>
                <button className="settings-button">Change Password</button>
              </div>
              <div className="settings-section">
                <h3>Preferences</h3>
                <label>
                  <input type="checkbox" defaultChecked />
                  Email notifications
                </label>
                <label>
                  <input type="checkbox" defaultChecked />
                  Two-factor authentication
                </label>
              </div>
            </div>
          )}

          {user.admin === 1 && activeTab === 'admin' && (
            <AdminPanelWithTabs
              user={user}
              adminToken={adminToken}
              jwtToken={jwtToken}
            />
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <p>&copy; 2026 TimeLock. Your Digital Legacy Platform.</p>
      </footer>
    </div>
  );
}

export default Dashboard;
